import React, { useEffect, useState, useCallback } from 'react'
import propTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components/macro'
import Typography from './typography'
import BodyParagraph from 'components/atoms/body-paragraph'
import data from 'utilities/data'
import EnhancedTable from 'components/organisms/enhanced-table'
import Button from 'components/atoms/button'
import AdStatusIcon from 'components/molecules/ad-status-icon'
import { publishPortal, unpublishPortal } from 'redux/actions/data'
import { useTranslation } from 'react-i18next'

const AdvertisementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizings.lvl2};
`

const TableContainer = styled.div`
  margin-top: ${({ theme }) => theme.sizings.lvl2};
  background-color: ${({ theme }) => theme.colors.alternativeBackground};
  border-radius: ${({ theme }) => theme.sizeByFactor(0.5)};
  overflow: hidden;
`

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizings.lvl1};
  height: 100%;
  min-height: ${({ theme }) => theme.sizings.lvl6};
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: ${({ theme }) => theme.sizings.lvl6};
`

const RowContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  min-height: ${({ theme }) => theme.sizings.lvl6};
`

function AdvertisementDetailActions({ onSubmit, carId }) {
  const [carDetails, setCarDetails] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const instanceId = useSelector((state) => state.auth.instanceId)
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const fetchPortalData = useCallback(async () => {
    if (!carId || !instanceId) {
      return
    }

    setIsLoading(true)
    try {
      const carFileResponse = await data.getCarfile(instanceId, {
        carId: carId,
      })
      if (Object.keys(carFileResponse).length) {
        setCarDetails(carFileResponse?.portals || [])
      }
    } catch (error) {
      console.error('Error fetching portal data:', error)
    } finally {
      setIsLoading(false)
    }
  }, [carId, instanceId])

  useEffect(() => {
    fetchPortalData()
  }, [fetchPortalData])

  const tableColumns = [
    {
      id: 'portal',
      label: 'Portal',
      width: 20,
    },
    {
      id: 'status',
      label: 'Status',
      width: 10,
    },
    {
      id: 'remarks',
      label: 'Opmerkingen',
      width: 50,
    },
    {
      id: 'actions',
      label: 'Acties',
      width: 20,
      alignRight: true,
    },
  ]

  const getTableRows = (portals) => {
    if (!portals || !portals.length) {
      return []
    }

    return portals.map((portal) => ({
      portal: {
        component: (
          <RowContainer>
            <Typography type="BodyParagraph">
              {portal.omschrijving}
              {portal.afkorting && ` (${portal.afkorting})`}
            </Typography>
          </RowContainer>
        ),
        data: portal.omschrijving,
      },
      status: {
        component: (
          <StatusContainer>
            <AdStatusIcon status={portal.status} />
          </StatusContainer>
        ),
      },
      remarks: {
        component: portal.statusLog && (
          <RowContainer>
            <BodyParagraph
              dangerouslySetInnerHTML={{ __html: portal.statusLog }}
            />
          </RowContainer>
        ),
        data: portal.statusLog || '',
      },
      actions: {
        component: (
          <ButtonContainer>
            <Button
              level="option"
              text={
                portal.status === 'ok'
                  ? t('advertisementView.unpublish')
                  : t('advertisementView.publish')
              }
              onClick={() => handleTogglePortalPublishAction(portal.id)}
              size="sm"
            />
          </ButtonContainer>
        ),
        data: portal.status === 'ok'
          ? t('advertisementView.unpublish')
          : t('advertisementView.publish'),
      },
    }))
  }

  const handleTogglePortalPublishAction = async (portalId) => {
    const selectedPortal = carDetails.find((p) => p.id === portalId)

    if (selectedPortal.status !== 'ok') {
      await dispatch(
        publishPortal('portalPublish', {
          auto_id: Number(carId),
          portal_ids: [Number(portalId)],
        }),
      )
    } else {
      await dispatch(
        unpublishPortal('portalUnpublish', {
          auto_id: Number(carId),
          portal_ids: [Number(portalId)],
        }),
      )
    }
    await fetchPortalData()
  }

  return (
    <AdvertisementContainer>
      <TableContainer>
        {isLoading ? (
          <Typography type="BodyParagraph">{t('loading')}</Typography>
        ) : carDetails && carDetails.length > 0 ? (
          <EnhancedTable
            columns={tableColumns}
            rows={getTableRows(carDetails)}
            selectable={false}
            noDataMessage={t('advertisementView.noPortalData')}
            verticalCellBorders={false}
          />
        ) : (
          <Typography type="BodyParagraph">
            {t('advertisementView.noPortalData')}
          </Typography>
        )}
      </TableContainer>
    </AdvertisementContainer>
  )
}

AdvertisementDetailActions.propTypes = {
  onSubmit: propTypes.func,
  carId: propTypes.string,
}

AdvertisementDetailActions.defaultProps = {
  onSubmit: null,
  carId: null,
}

export default AdvertisementDetailActions
